import { useEffect } from "react";
import { BundleWindow, NewBundleWindow } from "./BundleWindow";
import { usePriceList } from "./data";
import { GroupWindow } from "./GroupWindow";
import { ItemWindow } from "./ItemWindow";
import { NewItemWindow } from "./NewItemWindow";
import { NewSupplierWindow, SupplierWindow } from "./SupplierWindow";
import { isProduct, WINDOW_KINDS } from "./meta";
import { usePriceListWindows } from "./windows";

/*
 * The Price List's windows, over whatever page is showing (the app draws this on every page while
 * the address has one: see windows.ts). The Price List's data loads only while one is open.
 */

export default function PriceListWindowHost() {
    const { params } = usePriceListWindows();
    return WINDOW_KINDS.some((kind) => params.has(kind)) ? <OpenWindows /> : null;
}

function OpenWindows() {
    const { data } = usePriceList();
    const windows = usePriceListWindows();
    const { params, set } = windows;
    // Each in its own window: an item link to a product (a list, Home, a search) moves to the product's window, and the other way.
    const search = params.toString();
    useEffect(() => {
        if (!data) return;
        const kindOf = (uuid: string | null) => {
            const m = uuid ? data.materials.find((x) => x.uuid === uuid) : undefined;
            return m && (isProduct(m) ? "product" : "item");
        };
        const asItem = params.get("item");
        const asProduct = params.get("product");
        if (kindOf(asItem) === "product") set({ item: null, itemTab: null, product: asItem, productTab: null });
        else if (kindOf(asProduct) === "item") set({ product: null, productTab: null, item: asProduct, itemTab: null });
        // `search` stands for `params` (a new object each render).
    }, [data, search]);
    if (!data) return null;
    const item = params.get("item");
    const product = params.get("product");
    const supplier = params.get("supplier");
    const bundle = params.get("bundle");
    const group = params.get("group");

    return (
        <>
            {item === "new" && <NewItemWindow kind="item" onClose={() => set({ item: null })} onCreated={(uuid, built) => set({ item: uuid, itemTab: built ? "contents" : "pricing" })} />}
            {product === "new" && <NewItemWindow kind="product" onClose={() => set({ product: null })} onCreated={(uuid) => set({ product: uuid, productTab: "pricing" })} />}
            {item && item !== "new" ? (
                <ItemWindow
                    kind="item"
                    data={data}
                    uuid={item}
                    tab={params.get("itemTab") ?? "details"}
                    onTab={(t) => set({ itemTab: t })}
                    onClose={() => set({ item: null, itemTab: null })}
                />
            ) : null}
            {product && product !== "new" ? (
                <ItemWindow
                    kind="product"
                    data={data}
                    uuid={product}
                    tab={params.get("productTab") ?? "details"}
                    onTab={(t) => set({ productTab: t })}
                    onClose={() => set({ product: null, productTab: null })}
                />
            ) : null}
            {supplier === "new" && <NewSupplierWindow data={data} onClose={() => set({ supplier: null })} onCreated={(uuid) => set({ supplier: uuid, supplierTab: "details" })} />}
            {supplier && supplier !== "new" ? (
                <SupplierWindow
                    data={data}
                    uuid={supplier}
                    tab={params.get("supplierTab") ?? "details"}
                    onTab={(t) => set({ supplierTab: t })}
                    onClose={() => set({ supplier: null, supplierTab: null })}
                    onOpenItem={(uuid) => windows.openItem(uuid)}
                />
            ) : null}
            {bundle === "new" && <NewBundleWindow data={data} onClose={() => set({ bundle: null })} onCreated={(uuid) => set({ bundle: uuid, bundleTab: "contents" })} />}
            {bundle && bundle !== "new" ? (
                <BundleWindow
                    data={data}
                    uuid={bundle}
                    tab={params.get("bundleTab") ?? "details"}
                    onTab={(t) => set({ bundleTab: t })}
                    onClose={() => set({ bundle: null, bundleTab: null })}
                    onOpenItem={(uuid) => windows.openItem(uuid)}
                />
            ) : null}
            {group ? (
                <GroupWindow
                    data={data}
                    id={group}
                    tab={params.get("groupTab") ?? "details"}
                    onTab={(t) => set({ groupTab: t, rename: null })}
                    onClose={() => set({ group: null, groupTab: null, rename: null })}
                    rename={params.has("rename")}
                    onOpenItem={(uuid) => windows.openItem(uuid)}
                />
            ) : null}
        </>
    );
}
